// openLocationNavigator.js
// React-compatible async function to open navigation apps.
// 1) Waze, 2) Apple Maps (iOS), 3) Google Maps, with desktop-safe fallbacks.
// All URLs now consistently use LAT,LON order and properly URL-encoded commas for Waze.

export async function openLocationInNavigator(lat, lon, viewport = 17) {
	if (lat == null || lon == null) throw new Error('lat and lon are required');

	const latStr = encodeURIComponent(lat);
	const lonStr = encodeURIComponent(lon);
	const latLonEncoded = `${latStr}%2C${lonStr}`; // comma encoded for Waze URLs
	if ( typeof viewport === "number" ) viewport = String(viewport)+'z';

	const ua = navigator.userAgent || navigator.vendor || window.opera || '';
	const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
	const isAndroid = /Android/.test(ua);
	const isDesktop = !isIOS && !isAndroid;

	if (isDesktop) {
		// On desktop, open Google Maps web directly with proper viewport
		const desktopUrl = `https://www.google.com/maps/dir/${latStr},${lonStr}/@${latStr},${lonStr},${viewport}`;
		window.open(desktopUrl, '_blank');
		return { success: true, method: 'web_desktop', url: desktopUrl };
	}

	function tryOpen(appUrl, webUrl) {
		return new Promise((resolve) => {
			let opened = false;

			const timer = setTimeout(() => {
				if (!opened) {
					window.open(webUrl, '_blank');
					resolve({ success: true, method: 'web_fallback', url: webUrl });
				}
			}, 1500);

			const visibilityHandler = () => {
				if (document.hidden) {
					opened = true;
					clearTimeout(timer);
					document.removeEventListener('visibilitychange', visibilityHandler);
					resolve({ success: true, method: 'app', url: appUrl });
				}
			};

			document.addEventListener('visibilitychange', visibilityHandler);

			// Attempt to launch the app URL
			window.location.href = appUrl;
		});
	}

	// --- 1) Waze ---
	const wazeApp = `waze://?ll=${latLonEncoded}&navigate=yes`;
	const wazeWeb = `https://www.waze.com/ul?ll=${latLonEncoded}&navigate=yes`;
	const wazeResult = await tryOpen(wazeApp, wazeWeb);
	if (wazeResult.method === 'app') return { success: true, method: 'waze_app' };
	if (wazeResult.method.startsWith('web')) return { success: true, method: 'waze_web' };

	// --- 2) Apple Maps (iOS) ---
	if (isIOS) {
		const appleApp = `maps://?daddr=${latStr},${lonStr}&dirflg=d`;
		const appleWeb = `https://maps.apple.com/?daddr=${latStr},${lonStr}&dirflg=d`;
		const appleResult = await tryOpen(appleApp, appleWeb);
		if (appleResult.method === 'app') return { success: true, method: 'apple_maps_app' };
		if (appleResult.method.startsWith('web')) return { success: true, method: 'apple_maps_web' };
	}

	// --- 3) Google Maps ---
	if (isAndroid) {
		const googleApp = `google.navigation:q=${latStr},${lonStr}&mode=d`;
		const googleWeb = `https://google.com/maps/dir/${latStr},${lonStr}/@${latStr},${lonStr},${viewport}`;
		const googleResult = await tryOpen(googleApp, googleWeb);
		if (googleResult.method === 'app') return { success: true, method: 'google_maps_app_android' };
		return { success: true, method: 'google_maps_web' };
	} else {
		const googleApp = `comgooglemaps://?daddr=${latStr},${lonStr}&directionsmode=driving`;
		const googleWeb = `https://www.google.com/maps/dir/${latStr},${lonStr}/@${latStr},${lonStr},${viewport}`;
		const googleResult = await tryOpen(googleApp, googleWeb);
		if (googleResult.method === 'app') return { success: true, method: 'google_maps_app' };
		return { success: true, method: 'google_maps_web' };
	}
}

// Example React usage:
// function MyComponent() {
//   const handleNavigate = async () => {
//     const result = await openLocationInNavigator(42.249584, 12.890726);
//     console.log('Navigation result:', result);
//   };
//   return <button onClick={handleNavigate}>Open Navigation</button>;
// }

export default openLocationInNavigator;
